import { Avatar, Box, Typography, IconButton, AppBar, useTheme, Popper, ClickAwayListener, Card, Divider, Tooltip, Button } from "@mui/material"
import { Link, useNavigate } from "react-router-dom"
import { useMemo, useRef, useState } from "react"

import { useLayoutContext } from "../../../../shared/contexts/layout.context"
import { useThemeContext } from "../../../../shared/contexts/theme.context"
import { useUserStore } from "../../../store/user.store"
import { useAuthStore } from '../../../store/auth.store'
import aliveLogo from '../../../../shared/assets/alivelogo.png'
import simbaImg from '../../../../shared/assets/Habba-Logo.png'
import { Icons } from '../../../../shared/utils/icons.jsx'
import CustomSearchBar from '../../../../shared/components/common/CustomSearchBar'
import { allMenu } from './AdminSideDrawer'

const themeOptions = [
    { value: 'light', icon: Icons.default.LightMode, label: 'light' },
    { value: 'dark', icon: Icons.default.DarkMode, label: 'dark' },
    { value: 'system', icon: Icons.default.SystemTheme, label: 'system' },
]

export const EcosystemPortalCard = ( { isSideDrawerCollapsed } ) => {
    const theme = useTheme()
    const isDark = theme.palette.mode === 'dark'

    return (
        <Link
            to="/admin/dashboard"
            target="_placements"
            className="default-link"
            style={{ textDecoration: 'none', display: 'block', width: '100%', position: "sticky", bottom: 0, marginTop: "20px" }}
        >
            <Box
                sx={{
                    m: 1,
                    my: 0.5,
                    position: 'sticky',
                    bottom: 0,
                    overflow: 'hidden',
                    borderRadius: 2.5,
                    p: isSideDrawerCollapsed ? 0 : 1,
                    cursor: 'pointer',
                    width: '100%',

                    background: isDark
                        ? `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[900]} 100%)`
                        : `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.secondary.light} 100%)`,

                    color: theme.palette.text.primary,
                    border: '1px solid transparent',
                    borderColor: isDark
                        ? 'rgba(255, 255, 255, 0.12)'
                        : 'rgba(0, 0, 0, 0.08)',

                    boxShadow: isDark
                        ? '0 4px 20px rgba(0, 0, 0, 0.4)'
                        : '0 4px 20px rgba(0, 0, 0, 0.05)',

                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',

                    '&:hover': {
                        transform: 'translateY(-2px)',
                        borderColor: theme.palette.secondary.main,
                        boxShadow: isDark
                            ? `0 8px 25px ${theme.palette.secondary.main}33`
                            : `0 8px 25px ${theme.palette.secondary.main}20`,
                        '& .arrow-icon': {
                            transform: 'translate(3px, -3px)',
                            color: theme.palette.secondary.main,
                            bgcolor: isDark ? 'rgba(255, 255, 255, 0.1)' : theme.palette.action.hover,
                        },
                        '& .bg-glow': {
                            opacity: isDark ? 0.25 : 0.4,
                            transform: 'scale(1.2)',
                        },
                    },
                }}
            >
                {/* Background Glow */}
                <Box
                    className="bg-glow"
                    sx={{
                        position: 'absolute',
                        top: -20,
                        right: -20,
                        width: 100,
                        height: 100,
                        borderRadius: '50%',
                        background: theme.palette.secondary.main,
                        opacity: isDark ? 0.12 : 0.15,
                        filter: 'blur(25px)',
                        transition: 'all 0.5s ease',
                        pointerEvents: 'none',
                    }}
                />

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: 1 }}>
                    <Box sx={{ width: isSideDrawerCollapsed ? "100%" : "auto", display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        {/* Icon Container */}
                        <Tooltip arrow placement="right" title={isSideDrawerCollapsed ? "Switch to Placement Training" : ""}>
                            <Box
                                sx={{
                                    width: isSideDrawerCollapsed ? "100%" : "auto",
                                    p: 1.2,
                                    borderRadius: 2,
                                    bgcolor: isDark
                                        ? 'rgba(255, 255, 255, 0.06)'
                                        : 'rgba(0, 0, 0, 0.04)',
                                    color: theme.palette.secondary.main,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                {Icons.default.Building1}
                            </Box>
                        </Tooltip>

                        {!isSideDrawerCollapsed && <Box>
                            <Typography
                                noWrap
                                variant="subtitle2"
                                sx={{
                                    fontWeight: 600,
                                    lineHeight: 1.2,
                                    color: theme.palette.text.primary
                                }}
                            >
                                Placement Training
                            </Typography>
                        </Box>}
                    </Box>

                    {/* Action Icon */}
                    {!isSideDrawerCollapsed && <Box
                        className="arrow-icon"
                        sx={{
                            display: 'inline-flex',
                            p: 0.8,
                            borderRadius: '50%',
                            color: theme.palette.text.secondary,
                            bgcolor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
                            transition: 'all 0.3s ease',
                        }}
                    >
                        {Icons.default.ArrowOutward}
                    </Box>}
                </Box>
            </Box>
        </Link>
    )
}

const Navbar = ( { openSideMenu, closeSideMenu } ) => {

    const [settingsAnchorEl, setSettingsAnchorEl] = useState( null )

    const UserStore = useUserStore()
    const AuthStore = useAuthStore()

    const navigate = useNavigate()
    const { active, navHeight, navHeadText } = useLayoutContext()

    const { palette, border } = useTheme()
    const { setSelectedTheme, selectedTheme } = useThemeContext()

    const [searchText, setSearchText] = useState( "" )
    const [searchOpen, setSearchOpen] = useState( false )
    const searchRef = useRef( null )

    const searchableMenu = useMemo( () => {
        const user = UserStore.getUser
        const isAdmin = user.user_role?.toLowerCase() === 'admin'
        const hasPermission = permission => {
            if ( isAdmin || !permission ) return true
            return [permission].flat().some( p => user.user_permissions?.[p] )
        }
        const results = []
        for ( const item of allMenu ) {
            if ( !item.active || !item.link || item.type === 'divider' || item.type === 'category-header' || !hasPermission( item.permission ) ) continue
            if ( Array.isArray( item.children ) ) {
                item.children.forEach( ( child, index ) => {
                    results.push( { category: item.category, id: `${item.id}-${index}`, label: `${item.label} › ${child}`, link: item.link[index], icon: item.childrenIcons?.[index]?.default || item.icon } )
                } )
            } else {
                results.push( { category: item.category, id: item.id, label: item.label, link: item.link, icon: item.icon } )
            }
        }
        return results
    }, [UserStore.getUser] )

    const searchResults = useMemo( () => {
        const query = searchText.trim().toLowerCase()
        if ( !query ) return []
        return searchableMenu.filter( item => item.label.toLowerCase().includes( query ) )
    }, [searchText, searchableMenu] )

    const goToSearchResult = item => {
        navigate( item.link )
        setSearchText( "" )
        setSearchOpen( false )
    }

    const handleThemeChange = value => {
        setSelectedTheme( value )
        localStorage.setItem( 'preferedTheme', value )
        setSettingsAnchorEl( null )
    }

    return (
        <AppBar
            id="appbar"
            elevation={0}
            sx={{
                borderRadius: "0",
                background: "transparent",
                transition: "0.2s ease-out",
                position: "static",
                color: palette.nav.font,
                height: { sm: "50px", xs: "60px" },
                zIndex: "1220"
            }}
        >
            {/* <Typography fontSize="12px" sx={{ position: "absolute", top: 0, left: 0, right: 0, background: palette.warning.main, color: palette.warning.contrastText }}>Under maintenance</Typography> */}
            <Box
                id="app-bar"
                sx={{
                    alignItems: "center",
                    justifyContent: "space-between",
                    height: `${navHeight}px`,
                    padding: { sm: "0 20px 0 0", xs: "0 10px 0 10px" },
                    display: "flex",
                    maxWidth: "100%",
                    transition: "0.2s ease-out"
                }}>
                <Box
                    sx={{
                        display: "flex",
                        gap: "10px",
                        alignItems: "center"
                    }}>
                    <Box
                        sx={{
                            padding: "2px",
                            display: { sm: "none", xs: "flex" },
                            alignItems: "center",
                            justifyContent: "center",
                            borderRadius: "5px",
                            bgcolor: "white",
                            aspectRatio: "1 / 1"
                        }}>
                        <Box component="img" src={aliveLogo} alt="Logo" sx={{
                            width: "30px"
                        }} />
                    </Box>
                    <Box
                        component="img"
                        src={simbaImg}
                        sx={{
                            display: { sm: "block", xs: "none" },
                            width: "20px"
                        }} />
                    <Typography
                        component='h1'
                        color={palette.nav.font}
                        sx={{
                            display: { sm: "block", xs: "none" },
                            fontSize: "14px"
                        }}>
                        Acharya LMS
                    </Typography>
                    <Typography
                        component='span'
                        color={palette.nav.font}
                        sx={{
                            display: { sm: "block", xs: "none" },
                            fontSize: "14px"
                        }}>
                        |
                    </Typography>
                    <Typography color={palette.nav.font} component="span" sx={{
                        fontSize: { md: "14px", xs: "14px" }
                    }} >{navHeadText || `Welcome to digital classrooms`}</Typography>
                </Box>
                {<Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px"
                    }}>
                    <ClickAwayListener onClickAway={() => setSearchOpen( false )}>
                        <Box ref={searchRef} sx={{ display: { sm: "block", xs: "none" } }}>
                            <CustomSearchBar
                                width="260px"
                                border={`1px solid ${palette.primary.main}`}
                                style={{ ":focus-within": { outline: `1px solid ${palette.primary.main}` } }}
                                placeholder="Search menu..."
                                icon={Icons.default.SearchIcon}
                                value={searchText}
                                onChange={e => { setSearchText( e.target.value ); setSearchOpen( true ) }}
                                onFocus={() => setSearchOpen( true )}
                                onKeyDown={e => {
                                    if ( e.key === 'Enter' && searchResults.length > 0 ) {
                                        e.preventDefault()
                                        goToSearchResult( searchResults[0] )
                                    } else if ( e.key === 'Escape' ) setSearchOpen( false )
                                }}
                            />
                            <Popper sx={{ zIndex: 1300 }} placement='bottom-start' open={searchOpen && searchText.trim() !== ""} anchorEl={searchRef.current}>
                                <Card elevation={3} sx={{ mt: "5px", width: "260px", maxHeight: "300px", overflowY: "auto", border: border[1] }}>
                                    {searchResults.length === 0
                                        ? <Typography variant='body2' color='textSecondary' sx={{ p: 1.5, fontSize: "13px" }}>No results found</Typography>
                                        : searchResults.map( item => (
                                            <Box
                                                key={item.id}
                                                onClick={() => goToSearchResult( item )}
                                                sx={{ display: "flex", alignItems: "center", gap: "10px", px: 1.5, py: 0.75, cursor: "pointer", color: palette.text.primary, "&:hover": { bgcolor: palette.action.hover } }}>
                                                <Box sx={{ display: "flex", fontSize: "1.2", color: palette.text.secondary }}>{item.icon}</Box>
                                                <Box>
                                                    <Typography sx={{ fontSize: "0.875rem" }}>{item.label}</Typography>
                                                    <Typography noWrap component="span" sx={{ display: "block", mt: -0.3, color: "text.secondary", fontSize: "0.75rem" }}>{item.category}</Typography>
                                                </Box>
                                            </Box>
                                        ) )}
                                </Card>
                            </Popper>
                        </Box>
                    </ClickAwayListener>
                    <Box sx={{
                        display: { sm: "block", xs: "none" }
                    }}>
                        <Typography
                            variant='subtitle2'
                            sx={{
                                textAlign: "right",
                                textTransform: "capitalize",
                                color: palette.text.secondary
                            }}>{UserStore.getUser["user_name"] || ""}</Typography>
                    </Box>

                    {/* {( UserStore.getUser.user_role?.match( /^admin$/ig ) || ( UserStore.getUser.user_role?.match( /^subadmin$/ig ) && UserStore.getUser?.user_permissions?.auditing === true ) ) && <Box onClick={toggleAuditMode} display={{ md: "flex", xs: "none" }} gap="10px" alignItems="center" sx={{ cursor: "pointer" }} padding="5px 10px" border={border[1]} bgcolor={palette.common.bg} borderRadius="10px">
                        <Typography variant="subtitle2" color="primary">Audit Mode</Typography>
                        <CustomSwitch checked={active.startsWith( '/audit' )} />
                    </Box>} */}
                    <IconButton onClick={( e ) => setSettingsAnchorEl( e.currentTarget )}>
                        <Avatar src={UserStore.getUser.user_profile} sx={{ height: "32px", width: "32px" }}></Avatar>
                    </IconButton>

                    <Popper sx={{ zIndex: 100 }} placement='bottom' open={Boolean( settingsAnchorEl )} anchorEl={settingsAnchorEl}>
                        <ClickAwayListener onClickAway={() => setSettingsAnchorEl( null )}>
                            <Card sx={{ marginTop: "10px", marginRight: "20px", border: border[1], borderRadius: "5px" }} elevation={1} >
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "5px",
                                        padding: "5px"
                                    }}>
                                    <Avatar onClick={( e ) => setSettingsAnchorEl( e.currentTarget )} sx={{ height: "30px", width: "30px", borderRadius: "5px" }}></Avatar>
                                    <Box>
                                        <Typography
                                            variant='subtitle2'
                                            sx={{
                                                display: "block",
                                                lineHeight: 1.3,
                                                fontSize: "14px"
                                            }}>{UserStore.getUser["name"] || UserStore.getUser["user_name"] || UserStore.getUser["employee_name"] || UserStore.getUser["student_name"] || ""} </Typography>
                                        <Typography
                                            color='textSecondary'
                                            variant='subtitle2'
                                            sx={{
                                                display: "block",
                                                lineHeight: 1.3,
                                                fontSize: "12px"
                                            }}>{UserStore.getUser["AUID"] || UserStore.getUser["empcode"] || UserStore.getUser["user_id"] || ""}</Typography>
                                    </Box>
                                </Box>
                                <Divider />
                                <Box
                                    sx={{
                                        padding: "5px",
                                        justifyContent: "space-between",
                                        display: "flex",
                                        gap: "20px",
                                        alignItems: "center"
                                    }}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            gap: "10px",
                                            alignItems: "center"
                                        }}>
                                        {Icons.default.PaletteOutlined}
                                        <Typography
                                            variant="variant2"
                                            sx={{
                                                width: "max-content",
                                                display: "flex",
                                                fontSize: "12px"
                                            }}>
                                            Theme
                                        </Typography>
                                    </Box>
                                    <Box
                                        sx={{
                                            borderRadius: "20px",
                                            bgcolor: palette.contentBg,
                                            padding: "5px",
                                            display: "flex",
                                            gap: "2px",
                                            position: "relative",
                                            alignItems: "center"
                                        }}>

                                        <Box
                                            sx={{
                                                position: "absolute",
                                                bgcolor: palette.background.paper,
                                                borderRadius: "50%",
                                                top: "5px",
                                                bottom: "5px",
                                                left: `${5 + ( themeOptions.findIndex( to => to.value === selectedTheme ) * 30 )}px`,
                                                height: "calc(100% - 10px)",
                                                aspectRatio: "1 / 1",
                                                transition: "all 300ms"
                                            }} />

                                        {themeOptions.map( theme => (
                                            <Tooltip disableInteractive key={theme.value} title={theme.label} >
                                                <IconButton onClick={() => handleThemeChange( theme.value )} size='small' >
                                                    {theme.icon}
                                                </IconButton>
                                            </Tooltip>
                                        ) )}
                                    </Box>
                                </Box>
                                <Divider />
                                <EcosystemPortalCard />

                                <Box sx={{
                                    padding: "5px"
                                }}>
                                    <Link to={`/admin`}  ><Button onClick={() => AuthStore.doLogout()} startIcon={Icons.default.Logout} color='error' size='small' fullWidth variant='contained' disableElevation >Logout</Button></Link>
                                </Box>
                            </Card>
                        </ClickAwayListener>
                    </Popper>
                </Box>}
            </Box>
        </AppBar>
    )
}

export default Navbar
